import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormHelperText,
  Card,
  CardContent,
  Link as MuiLink,
} from "@mui/material";
import {
  useCreateSuspectMutation,
  useLazySearchCustomerQuery,
} from "../../store/services/customerService";
import { useNavigate, Link } from "react-router-dom";
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import type { CustomerModel, CreditsafeReturnModel } from "../../store/types/customer.types";
import { CustomerTypeEnum } from "../../store/types/customer.types";
import { formatOrgNumber, isValidOrgNumber } from "./orgNumber";

const getCustomerTypeLabel = (type: CustomerTypeEnum): string => {
  switch (type) {
    case CustomerTypeEnum.Normal:
      return "Kund";
    case CustomerTypeEnum.Suspect:
      return "Suspekt";
    case CustomerTypeEnum.Prospect:
      return "Prospekt";
    default:
      return "Okänd";
  }
};

// Props för QuickAction-komponenten
interface CreateSuspectDialogProps {
  open: boolean;
  onClose: () => void;
  defaultOrgNumber?: string;
}

export const CreateSuspectDialog: React.FC<CreateSuspectDialogProps> = ({
  open,
  onClose,
  defaultOrgNumber,
}) => {
  const [orgNumber, setOrgNumber] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<CustomerModel[] | null>(null);
  const [creditsafeResults, setCreditsafeResults] = useState<CreditsafeReturnModel[] | null>(null);
  const navigate = useNavigate();

  const [createSuspect, { isLoading, error }] = useCreateSuspectMutation();
  const [searchCustomer, { isFetching: isSearching }] = useLazySearchCustomerQuery();
  const lastSearchedOrg = useRef<string>("");

  const customerExists = searchResults?.some((c) => !!c.CustomerId) ?? false;

  const triggerSearch = async (digitsOnly: string) => {
    if (lastSearchedOrg.current === digitsOnly) return;
    lastSearchedOrg.current = digitsOnly;

    try {
      const result = await searchCustomer({
        CustomerEntityOrgId: digitsOnly,
        fetchFromCreditsafe: true,
      }).unwrap();
      setSearchResults(result.CustomerReturnModel ?? []);
      setCreditsafeResults(result.CreditsafeReturnModel ?? []);
    } catch {
      setSearchResults(null);
      setCreditsafeResults(null);
    }
  };

  // Förifyll orgnummer (t.ex. från kundsöket) när dialogen öppnas
  useEffect(() => {
    if (!open || !defaultOrgNumber) return;

    const digitsOnly = defaultOrgNumber.replace(/\D/g, "");
    setOrgNumber(formatOrgNumber(digitsOnly));
    setValidationError(null);
    setSearchResults(null);
    setCreditsafeResults(null);
    lastSearchedOrg.current = "";

    if (isValidOrgNumber(digitsOnly)) {
      triggerSearch(digitsOnly);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultOrgNumber]);

  const handleOrgNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setOrgNumber(value);
    setValidationError(null);
    setSearchResults(null);
    setCreditsafeResults(null);

    const digitsOnly = value.replace(/\D/g, "");
    if (isValidOrgNumber(digitsOnly)) {
      triggerSearch(digitsOnly);
    }
  };

  const handleBlur = () => {
    if (orgNumber) {
      setOrgNumber(formatOrgNumber(orgNumber));
    }
  };

  const handleCreate = async () => {
    const formattedOrgNr = orgNumber.replace(/[- ]/g, "");

    if (!formattedOrgNr) {
      setValidationError("Organisationsnummer måste anges");
      return;
    }

    if (!isValidOrgNumber(formattedOrgNr)) {
      setValidationError("Ogiltigt organisationsnummer. Kontrollera formatet.");
      return;
    }

    try {
      const result = await createSuspect({ orgId: formattedOrgNr }).unwrap();
      if (result.CustomerId) {
        navigate(`/customer/${result.CustomerId}?created=true`);
      }
      onClose();
    } catch (err) {
      console.error("Failed to create suspect:", err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Skapa ny suspekt</DialogTitle>
      <DialogContent>
        <Box sx={{ my: 2 }}>
          <Typography variant="body1" gutterBottom>
            Ange ett organisationsnummer för att skapa en ny suspekt.
          </Typography>

          <TextField
            label="Organisationsnummer"
            fullWidth
            variant="outlined"
            value={orgNumber}
            onChange={handleOrgNumberChange}
            error={!!validationError}
            helperText={validationError}
            placeholder="XXXXXX-XXXX"
            margin="normal"
            InputProps={{
              autoFocus: true,
              endAdornment: isSearching ? (
                <CircularProgress size={20} />
              ) : null,
            }}
            onBlur={handleBlur}
          />

          <FormHelperText>
            Ange organisationsnummer i format XXXXXX-XXXX eller XXXXXXXX-XXXX
          </FormHelperText>
        </Box>

        {isSearching && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <SearchIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              Söker efter kunduppgifter...
            </Typography>
          </Box>
        )}

        {searchResults !== null && !isSearching && searchResults.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {customerExists ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Organisationsnumret finns redan som kund i systemet. Du kan inte
                skapa en ny suspekt för detta nummer.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                Följande information hittades för organisationsnumret.
              </Alert>
            )}

            {searchResults.map((customer, index) => (
              <Card key={index} variant="outlined" sx={{ mb: 1 }}>
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {customer.CustomerName || customer.CompanyName}
                  </Typography>
                  {customer.CompanyName &&
                    customer.CompanyName !== customer.CustomerName && (
                      <Typography variant="body2" color="text.secondary">
                        Bolag: {customer.CompanyName}
                      </Typography>
                    )}
                  {customer.Address && (
                    <Typography variant="body2" color="text.secondary">
                      Adress: {customer.Address}
                    </Typography>
                  )}
                  {customer.EMail && (
                    <Typography variant="body2" color="text.secondary">
                      E-post: {customer.EMail}
                    </Typography>
                  )}
                  {customer.LegalPersonNumber && (
                    <Typography variant="body2" color="text.secondary">
                      Org.nr: {customer.LegalPersonNumber}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Typ: {getCustomerTypeLabel(customer.CustomerType)}
                  </Typography>
                  {customer.CustomerNo && (
                    <MuiLink
                      component={Link}
                      to={`/customer/${customer.CustomerNo}`}
                      onClick={onClose}
                      sx={{ mt: 1, display: "inline-block" }}
                    >
                      Gå till kundprofil
                    </MuiLink>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {!isSearching && creditsafeResults && creditsafeResults.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Uppgifter hittades via Creditsafe.
            </Alert>
            {creditsafeResults.map((cs, index) => (
              <Card key={index} variant="outlined" sx={{ mb: 1 }}>
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {cs.Name}
                  </Typography>
                  {cs.OrgNr && (
                    <Typography variant="body2" color="text.secondary">
                      Org.nr: {cs.OrgNr}
                    </Typography>
                  )}
                  {cs.Address && (
                    <Typography variant="body2" color="text.secondary">
                      Adress: {[cs.Address.Street, cs.Address.ZipCode, cs.Address.Town].filter(Boolean).join(", ")}
                    </Typography>
                  )}
                  {cs.StatusText && (
                    <Typography variant="body2" color="text.secondary">
                      Status: {cs.StatusText}
                    </Typography>
                  )}
                  {cs.CompanyTypeText && (
                    <Typography variant="body2" color="text.secondary">
                      Bolagsform: {cs.CompanyTypeText}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {searchResults !== null && !isSearching && searchResults.length === 0 && (!creditsafeResults || creditsafeResults.length === 0) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Inga befintliga kunder hittades för detta organisationsnummer.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Ett fel uppstod när suspekten skulle skapas. Försök igen senare.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Avbryt
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          color="primary"
          disabled={isLoading || !orgNumber || customerExists}
          startIcon={isLoading ? <CircularProgress size={20} /> : <AddIcon />}
        >
          Skapa suspekt
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// QuickAction-knappen som öppnar dialogen
interface CreateSuspectButtonProps {
  variant?: "text" | "outlined" | "contained";
  size?: "small" | "medium" | "large";
  fullWidth?: boolean;
}

const CreateSuspectButton: React.FC<CreateSuspectButtonProps> = ({
  variant = "contained",
  size = "medium",
  fullWidth = false,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  
  return (
    <>
      <Button
        variant={variant}
        color="primary"
        startIcon={<AddIcon />}
        onClick={() => setDialogOpen(true)}
        size={size}
        fullWidth={fullWidth}
      >
        Ny suspekt
      </Button>
      <CreateSuspectDialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
      />
    </>
  );
};

export default CreateSuspectButton;